import { queryClient } from '@/router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo } from 'react'
import { toast } from 'sonner'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'
import { activateCasesMutation } from '@/lib/queries/business/v1/case/active/mutations'
import { archiveCasesMutation } from '@/lib/queries/business/v1/case/archive/mutations'
import { exportCasesMutation } from '@/lib/queries/business/v1/case/export/mutations'
import { getMembersQuery } from '@/lib/queries/business/v1/user/queries'
import { getSearchCasesQuery } from '@/lib/queries/business/v4/case/search/queries'
import { normalizeArraySearchParam } from '@/lib/utils/search-param'

import { useCasesTableContext } from '../cases/contexts'
import { CaseStatusActionToastContent } from '../cases/ui/case-status-action-toast-content/case-status-action-toast-content'
import { type CasesFilterChangeHandler } from '../cases/ui/cases-filter/cases-filter'
// import { normalizeArraySearchParam } from '@/lib/utils/search-param'
import { type CasesSort } from '../types'
import { useCaseRowSelection } from '../ui/cases/hooks/use-case-row-selection'
import { scrollAppPageToTop } from '../utils'

export const useCasesPage = () => {
  const { tableType } = useCasesTableContext()
  const navigate = useNavigate({
    from: tableType === 'active' ? '/cases-eoir/active' : '/cases-eoir/inactive',
  })

  const searchParams = useSearch({
    from: tableType === 'active' ? '/_app/cases-eoir/active' : '/_app/cases-eoir/inactive',
  })
  const {
    page,
    search = '',
    tags,
    tagCondition,
    statuses,
    assignees,
    assigneeCondition,
    sortKey,
    sortOrder,
  } = searchParams

  const pageSize = useMemo(() => {
    if (searchParams.pageSize) {
      return searchParams.pageSize
    }

    const persisted = localStorage.getItem(LOCAL_STORAGE_KEY_MAP.casesPageSize)

    return Number(persisted) || 10
  }, [searchParams.pageSize])

  const normalizedTags = useMemo(() => normalizeArraySearchParam(tags), [tags])
  const normalizedStatuses = useMemo(() => normalizeArraySearchParam(statuses), [statuses])
  const normalizedAssignees = useMemo(() => normalizeArraySearchParam(assignees), [assignees])

  const {
    data: dataCases,
    isLoading: isLoadingCases,
    isFetching: isFetchingCases,
    isError: isErrorInCasesSearch,
  } = useQuery(
    getSearchCasesQuery({
      isEoir: true,
      pageNumber: Number(page),
      pageSize,
      keyword: search,
      tagCondition,
      tags: normalizedTags,
      status: normalizedStatuses,
      assignees: assignees?.map((assigneeId) => Number(assigneeId)),
      assigneeCondition,
      sort: sortKey,
      asc: sortOrder === 'asc',
      archiveStatus: tableType === 'active' ? CASE_ARCHIVE_STATUS_MAP.active : CASE_ARCHIVE_STATUS_MAP.archived,
    })
  )
  const { data: dataOrganizationMembers } = useQuery(getMembersQuery)
  const { mutateAsync: archiveCases, isPending: isArchivingCases } = useMutation(archiveCasesMutation)
  const { mutateAsync: activateCases, isPending: isActivatingCases } = useMutation(activateCasesMutation)
  const { mutateAsync: exportCases, isPending: isExportingCases } = useMutation(exportCasesMutation({ isEoir: true }))

  const caseIds = useMemo(() => dataCases?.cases?.map((x) => x.case_id) ?? [], [dataCases?.cases])

  const { selection, isEveryRowSelected, handleToggleAllRowSelection, handleToggleCaseRowSelection } =
    useCaseRowSelection({ ids: caseIds })

  const handlePaginationChange = useCallback(
    async (page: number, pageSize: number) => {
      localStorage.setItem(LOCAL_STORAGE_KEY_MAP.casesPageSize, String(pageSize))

      await navigate({
        search: {
          ...searchParams,
          page,
          pageSize,
          search,
          // tags: normalizedTags,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, search, searchParams]
  )

  const handleFilterChange: CasesFilterChangeHandler = useCallback(
    async ({ keyword, tags, tagFilterOption, statuses, assignees, assigneeFilterOption }) => {
      await navigate({
        search: {
          ...searchParams,
          page: 1,
          pageSize,
          search: keyword,
          tags,
          tagCondition: tagFilterOption,
          statuses,
          assignees,
          assigneeCondition: assigneeFilterOption,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, pageSize, searchParams]
  )

  const handleSortChange = useCallback(
    async (sort: CasesSort) => {
      await navigate({
        search: {
          ...searchParams,
          page,
          pageSize,
          search,
          sortKey: sort.key,
          sortOrder: sort.order,
          tags: normalizedTags,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, normalizedTags, page, pageSize, search, searchParams]
  )

  const handleExportCsv = useCallback(async () => {
    await exportCases({
      isEoir: true,
      archiveStatus: tableType === 'active' ? CASE_ARCHIVE_STATUS_MAP.active : CASE_ARCHIVE_STATUS_MAP.archived,
    })
  }, [exportCases, tableType])

  const handleClickInactivateButton = async () => {
    await archiveCases(
      {
        caseIds: selection,
      },
      {
        onSuccess: async () => {
          toast(
            <CaseStatusActionToastContent
              changedStatus="inactive"
              caseIds={selection}
              isPlural={selection.length > 1}
            />,
            {
              duration: 8000,
              className: 'w-fit',
            }
          )
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v4.case.search.index],
          })
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v2.user.index],
          })
        },
      }
    )
  }

  const handleClickActivateButton = async () => {
    await activateCases(
      {
        caseIds: selection,
      },
      {
        onSuccess: async () => {
          toast(
            <CaseStatusActionToastContent changedStatus="active" caseIds={selection} isPlural={selection.length > 1} />,
            {
              duration: 8000,
              className: 'w-fit',
            }
          )
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v4.case.search.index],
          })
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v2.user.index],
          })
        },
      }
    )
  }

  const isLoadingCaseStatusActionButton = isActivatingCases || isArchivingCases

  useEffect(
    function closeAllToastsWhenNavigating() {
      toast.dismiss()
    },
    [tableType]
  )

  return {
    tableType,
    search,
    pageSize,
    dataCases,
    dataOrganizationMembers,
    normalizedTags,
    tagFilterOption: tagCondition,
    normalizedStatuses,
    normalizedAssignees,
    assigneeFilterOption: assigneeCondition,
    selection,
    sortKey,
    sortOrder,
    isLoadingCases,
    isFetchingCases,
    isErrorInCasesSearch,
    isEveryRowSelected,
    isExportingCases,
    isLoadingCaseStatusActionButton,
    handleToggleAllRowSelection,
    handleToggleCaseRowSelection,
    handlePaginationChange,
    handleFilterChange,
    handleSortChange,
    handleClickActivateButton,
    handleClickInactivateButton,
    handleExportCsv,
  }
}
